/**
 * Parent → iframe: sends the same access token used for API calls (see FileTransferServiceClient).
 * Child listens with: window.addEventListener('message', (e) => { if (e.data?.type === TOKEN_RECEIVED) ... })
 *
 * Prefer a concrete targetOrigin (iframe URL origin), not '*'.
 */
export const IFRAME_ACCESS_TOKEN_MESSAGE_TYPE = 'TOKEN_RECEIVED' as const;

/** Optional: iframe requests a (re-)send from parent */
export const REQUEST_IFRAME_ACCESS_TOKEN_MESSAGE_TYPE = 'REQUEST_ACCESS_TOKEN' as const;

export type IframeAccessTokenPayload = {
    type: typeof IFRAME_ACCESS_TOKEN_MESSAGE_TYPE;
    token: string | null;
    timestamp: number;
};

export const IFRAME_ACCESS_TOKEN_DOM_EVENT = 'backoffice:post-checkout-insights-iframe-access-token-postmessage';

export type IframeAccessTokenDomEventDetail = {
    targetOrigin: string;
    hasToken: boolean;
};

function readAccessToken(): string | null {
    return localStorage.getItem('access_token');
}

export function getOriginFromIframeSrc(src: string): string | null {
    try {
        return new URL(src, typeof window !== 'undefined' ? window.location.href : undefined).origin;
    } catch {
        return null;
    }
}

/**
 * Sends postMessage to the iframe's window. Dispatches a same-origin CustomEvent (without the token).
 */
export function postAccessTokenToIframe(targetWindow: Window | null | undefined, targetOrigin: string): boolean {
    if (!targetWindow || !targetOrigin) {
        return false;
    }

    const token = readAccessToken();
    const payload: IframeAccessTokenPayload = {
        type: IFRAME_ACCESS_TOKEN_MESSAGE_TYPE,
        token,
        timestamp: Date.now(),
    };

    targetWindow.postMessage(payload, targetOrigin);

    window.dispatchEvent(
        new CustomEvent<IframeAccessTokenDomEventDetail>(IFRAME_ACCESS_TOKEN_DOM_EVENT, {
            detail: { targetOrigin, hasToken: Boolean(token) },
        })
    );

    return true;
}
